import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, Optional } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { ImageModule } from '@df/module-image/image.module';
import { UgcImage } from '@df/ugc-image/ugc-image';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { compact } from 'lodash-es';
import uniqBy from 'lodash-es/uniqBy';
import { Product } from '../../../catalog/product/product.decorator';
import { UgcImageItemComponent } from '../item/ugc-image-item.component';
import { UgcImageService } from '../ugc-image.service';
import { UgcImageGalleryCmsProductViewComponent } from './cms-product-view/ugc-image-gallery-cms-product-view.component';
import { NUgcImageGallery } from './ugc-image-gallery.namespace';
let uid = 0;
@Component({
    selector: 'ugc-image-gallery',
    templateUrl: './ugc-image-gallery.html',
    styleUrl: './ugc-image-gallery.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        SizeClassDirective,
        ImageModule,
        ICmsClientModule,
        CarouselModule,
        TranslatePipe,
        UgcImageItemComponent
    ],
})
export class UgcImageGalleryComponent implements OnChanges {
    loadImagesStatus = new Status();
    images: UgcImage[] = [];
    uid = `ugc-carousel-${uid++}`;
    @Input()
    content?: NUgcImageGallery.IData;
    @Input()
    product?: Product<IProductDataV3>;
    constructor(
        protected ugcImageService: UgcImageService,
        private changeDetectorRef: ChangeDetectorRef,
        protected requestService: RequestService,
        public client: Client,
        @Optional() protected ugcImageGalleryCmsProductViewComponent: UgcImageGalleryCmsProductViewComponent
    ) {}
    ngOnChanges(): void {
        this.updateImages();
        this.detectChanges();
    }
    private updateImages(): void {
        this.requestService.send(
            this.loadImagesStatus,
            () => {
                return new Promise((resolve, reject) => {
                    this.ugcImageService
                        .loadImages(this.getUgcImageCategories(), this.getProductsSkus(), 1000, this.getRangeCodesNh(), this.getRooms())
                        .then(ugcImages => {
                            resolve({ data: ugcImages });
                        }, reject);
                });
            },
            (ugcImages: UgcImage[] | undefined) => {
                const filteredUgcImages = uniqBy(ugcImages, obj => obj.data?.image);
                this.finish(filteredUgcImages);
            },
            () => {
                this.finish([]);
            }
        );
    }
    protected finish(ugcImages: UgcImage[]): void {
        this.images = ugcImages;
        this.ugcImageGalleryCmsProductViewComponent?.updateVisibility?.();
        this.detectChanges();
    }
    private getProductsSkus(): string[] {
        if (this.product) {
            return compact(this.product.family.map(i => i.data?.sku));
        }
        return [];
    }
    private getRangeCodesNh(): number[] {
        const rangeCodes = this.content?.nyw_item_range_code_nh;
        if (rangeCodes) {
            return (Array.isArray(rangeCodes) ? rangeCodes : [rangeCodes]).filter(i => typeof i === 'number');
        }
        return [];
    }
    private getRooms(): string[] {
        const rooms = this.content?.room;
        if (rooms) {
            return (Array.isArray(rooms) ? rooms : [rooms]).filter(i => typeof i === 'string');
        }
        return [];
    }

    private getUgcImageCategories(): number[] {
        const categories = this.content?.ugc_category;
        if (categories) {
            return (Array.isArray(categories) ? categories : [categories]).filter(i => typeof i === 'number');
        }
        return [];
    }
    trackByImageId(index: number, image: UgcImage): number {
        return image.id;
    }
    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
    get hasImageGallery(): boolean {
        return !!this.images.length;
    }

    get slidesPerView() {
        const max = [2, 3, 5, 5][this.client.sizeId];
        const min = [2, 3, 4, 4][this.client.sizeId];
        const base = Math.min(max, Math.max(min, this.images.length));
        return base < this.images.length ? base + 0.3 : base;
    }
}
